/**
 * "Back to workspace" from a focused frame. A chat opened over other focused
 * frames returns to the frame it came from; otherwise focus ends and the
 * workspace shows the frame the chat belongs with.
 */
import { conversationFrameTaskId } from "@/components/shapes/conversation-frame/conversation-frame-utils";
import { activateFrameInCurrentContext } from "@/lib/focus-frame-activation";
import { focusAdapterFor } from "@/lib/focus/focus-adapter";
import { focusRendererKind } from "@/lib/focus/focus-renderer-kind";
import type { PresentationState } from "@/lib/focus/focus-types";
import type { FrameData } from "@/lib/frame-data";
import { isTauri } from "@/lib/is-tauri";
import { exitPresentationMode } from "@/lib/presentation-mode";
import { frameNodeHidden } from "@/stores/canvas-frame-geometry";
import { useCanvasStore } from "@/stores/canvas-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import {
  isFrameHiddenByMetadata,
  selectFramesByType,
} from "@/stores/frames-selectors";
import { useFramesStore } from "@/stores/frames-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import { useTaskPocketStore } from "@/stores/task-pocket-store";

/** The board the task pocket is anchored to while it shows this task. */
function pocketBoardId(taskId: string | null | undefined): string | null {
  const pocket = useTaskPocketStore.getState();
  const anchor = taskId === pocket.taskId ? pocket.anchor : null;
  return anchor?.kind === "board" ? anchor.frameId : null;
}

/** The focused frame a chat returns to, if one can be shown again. */
function focusReturnFrameId(
  presentation: PresentationState,
  frameId: string,
  boardId: string | null,
): string | null {
  const { framesById } = useFramesStore.getState();
  // Focus-hidden frames count: the focus can bring them back.
  const available = (id: string | null | undefined): id is string => {
    if (!id || id === frameId) return false;
    const candidate = framesById[id];
    return (
      !!candidate &&
      !isFrameHiddenByMetadata(candidate) &&
      candidate.frameType !== "conversation"
    );
  };
  const layout = presentation.customFocusLayout;
  const expandedId =
    layout?.kind === "conversation_companion_stack"
      ? layout.expandedStackFrameId
      : undefined;
  const boardIsValid =
    !!boardId && framesById[boardId]?.frameType === "taskboard";
  const candidates = [
    presentation.returnFrameId,
    expandedId,
    boardIsValid ? boardId : null,
    presentation.frameIds[presentation.activeIndex],
  ];
  return candidates.find(available) ?? null;
}

/** The selected frame, else the chat's board, the workspace shows a chat by. */
function workspaceFrameForChat(
  frameId: string,
  boardId: string | null,
): string | undefined {
  const frames = useFramesStore.getState();
  const { focusHiddenFrameIds } = useCanvasStore.getState();
  const shown = (candidate: FrameData) =>
    !frameNodeHidden(candidate, focusHiddenFrameIds);
  const { selectedFrameId } = useSelectionStore.getState();
  const selected = selectedFrameId
    ? frames.framesById[selectedFrameId]
    : undefined;
  const previous =
    selected &&
    selected.frameId !== frameId &&
    selected.frameType !== "conversation" &&
    shown(selected)
      ? selected
      : undefined;
  const boards = selectFramesByType(frames, "taskboard").filter(shown);
  const board =
    boards.find((candidate) => candidate.frameId === boardId) ??
    (boards.length === 1 ? boards[0] : undefined);
  return (previous ?? board)?.frameId;
}

export function backToWorkspace(
  frameId: string,
  options: {
    /** The frame's data when the frames store may not hold it. */
    frame?: FrameData;
    /** A chat may return to the focused frame it was opened from. */
    returnInFocus?: boolean;
    /** Runs before focus ends, e.g. closing the frame's own window. */
    beforeExit?: () => void;
  } = {},
): void {
  const frame = options.frame ?? useFramesStore.getState().framesById[frameId];
  const isConversation = frame?.frameType === "conversation";
  const boardId =
    isConversation && frame
      ? pocketBoardId(conversationFrameTaskId(frame))
      : null;
  const presentation = usePresentationStore.getState().presentation;
  if (
    options.returnInFocus &&
    isConversation &&
    presentation?.variant === "focus"
  ) {
    const returnFrameId = focusReturnFrameId(presentation, frameId, boardId);
    if (returnFrameId && activateFrameInCurrentContext(returnFrameId)) return;
  }
  const adapter = focusAdapterFor(
    presentation?.renderer ?? focusRendererKind(),
  );
  options.beforeExit?.();
  exitPresentationMode();
  if (isTauri()) useDesktopMode.getState().openWorkspace();
  if (!isConversation) return;
  const destinationId = workspaceFrameForChat(frameId, boardId);
  if (destinationId) adapter.revealOnWorkspace(destinationId);
}
